import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { HubUiModule } from '@atpco/hub-ui';
import { environment } from '@hub-env/environment';
import { UmsService } from '@hub-services/ums.service';
import { jwtDecode } from 'jwt-decode';
import { AuthService } from '../auth.service';

@Component({
  selector: 'rh-registration-complete',
  standalone: true,
  imports: [CommonModule, RouterModule, HubUiModule, FormsModule],
  templateUrl: './registration-complete.component.html',
  styleUrl: './registration-complete.component.scss'
})
export class RegistrationCompleteComponent implements OnInit {

  countdown = 3;
  timer: any;
  title = 'Signup Successful 🎉 - Redirecting...';
  message = 'You will be redirected to the login page in ';
  isFailed = false;
  isSuccess = true;
  isExpired = false;
  loggedIn = false;

  email: string = '';

  constructor(private authService: AuthService, private router: Router, private umsService: UmsService) {}

  ngOnInit(): void {
    // Check URL parameters first before starting countdown
    this.checkRegistrationStatus();
  }

  checkRegistrationStatus(): void {
    const fragment = window.location.hash.substring(1); // remove the leading #
    const params = new URLSearchParams(fragment);
    const idToken = params.get('id_token');
    const error = params.get('error');
    const errorDescription = params.get('error_description');
    if (this.authService.getActiveAccount()) {
      this.loggedIn = true;
      this.title = 'Already Logged In';
      this.message = 'You are already logged in. Redirecting to the home page';
      this.router.navigate(['/']);
      return;
    }

    if (error && errorDescription) {
      console.error('Error:', error);
      console.error('Error Description:', errorDescription);
      this.isSuccess = false;
      this.isFailed = true;
      
      // Check if error is related to an expired link
      if (errorDescription.toLowerCase().includes('expired')) {
        this.title = 'Link Expired';
        this.message = 'Your registration link has expired. Please enter your email below to request a new registration email.';
        this.isExpired = true;
      } else {
        this.title = 'Registration Failed';
        this.message = 'An error occurred during registration. Please request a new signup link.';
      }
      return;
    }

    if (idToken) {
      this.isSuccess = true;
      this.isFailed = false;
      this.startCountdown();
    } else {
      this.isSuccess = false;
      this.isFailed = true;
      this.title = 'Registration Failed';
      this.message = 'No valid registration information found. Please request a new signup link.';
      console.warn('No ID token found in URL.');
    }
  }

  startCountdown(): void {
    this.timer = setInterval(() => {
      this.countdown--;
      const el = document.getElementById('countdown');
      if (el) el.innerText = String(this.countdown);

      if (this.countdown <= 0) {
        clearInterval(this.timer);
        this.redirectToLogin();
      }
    }, 1000);
  }

  redirectToLogin() {
    // Only proceed with login redirect if we have a successful registration
    if (!this.isSuccess) return;

    const fragment = window.location.hash.substring(1);
    const params = new URLSearchParams(fragment);
    const idToken = params.get('id_token');

    const payload = jwtDecode(idToken ?? '') as any;

    // Post-registration redirect: force fresh login (prompt=login, no SSO),
    // pre-fill the just-registered email. Overrides the guard config defaults.
    this.authService.loginRedirect({
      scopes: [...environment.apiConfig.scopes],
      extraQueryParameters: {
        prompt: 'login',
        login_hint: encodeURIComponent(payload?.email),
      },
      redirectUri: environment.redirectUri,
    });
  }

  validateAndRequestEmail(): void {
    this.umsService.getAllUsersFromUMS().subscribe(users => {
      // TODO(strict-typing): cascades from the pre-existing untyped `getAllUsersFromUMS()` return.
      const user = users.find((u: any) => u.email?.toLowerCase().trim() === this.email.toLowerCase());
      if (user) {
        this.umsService.sendUmsInviteEmail(user.id).subscribe(() => {
          this.title = 'Invitation Sent';
          this.message = 'An invitation email has been sent to ' + this.email + '. Please check your inbox.';
        });
      } else {
        this.title = 'Email Not Found';
        this.message = 'The provided email address was not found. Please check and try again.';
        this.isExpired = false;
      }
    });
  }

  returnToLoginPage() {
    this.authService.loginRedirect({
      scopes: [...environment.apiConfig.scopes],
    });
  }
}
